import React, { useState, useEffect, useRef, useCallback } from 'react';
import SinFichero from './SinFichero';
import { getSignedUrl } from '../../../../../services/s3Service';

const AudioMessage = ({ details }) => {
  const { audioUrl, maytapiAudioUrl, s3Key, filename, mimeType, caption } = details;
  const [currentAudioUrl, setCurrentAudioUrl] = useState(audioUrl);
  const isRenewingRef = useRef(false);
  const retryCountRef = useRef(0);
  const maxRetries = 2;

  // Function to renew S3 signed URL
  const renewSignedUrl = useCallback(async () => {
    if (!s3Key || isRenewingRef.current) return null;

    isRenewingRef.current = true;
    try {
      const signedUrl = await getSignedUrl(s3Key);
      if (signedUrl) {
        retryCountRef.current = 0; // Reset retry count on success
        isRenewingRef.current = false;
        return signedUrl;
      }
    } catch (error) {
      console.error('Error renewing signed URL:', error);
    }
    isRenewingRef.current = false;
    return null;
  }, [s3Key]);

  useEffect(() => {
    // If we have an S3 key, always try to get a fresh signed URL
    // This ensures we have a valid URL even if the initial one is expired
    if (s3Key) {
      const initializeUrl = async () => {
        const freshUrl = await renewSignedUrl();
        if (freshUrl) {
          setCurrentAudioUrl(freshUrl);
        } else if (maytapiAudioUrl) {
          // Fallback to MayTapi URL only if we couldn't get S3 URL
          setCurrentAudioUrl(maytapiAudioUrl);
        } else if (audioUrl) {
          // Use original audioUrl as last resort
          setCurrentAudioUrl(audioUrl);
        }
      };
      initializeUrl();
    } else if (maytapiAudioUrl) {
      // Fallback to MayTapi URL if no S3 key
      setCurrentAudioUrl(maytapiAudioUrl);
    } else if (audioUrl) {
      // Use original audioUrl as last resort
      setCurrentAudioUrl(audioUrl);
    }
  }, [s3Key, renewSignedUrl, maytapiAudioUrl, audioUrl]);

  const handleAudioError = async () => {
    // If S3 URL fails and we have an s3Key, try to renew it
    if (s3Key && retryCountRef.current < maxRetries) {
      retryCountRef.current += 1;
      const renewedUrl = await renewSignedUrl();
      if (renewedUrl) {
        setCurrentAudioUrl(renewedUrl);
        return;
      }
    }

    // If renewal failed or no s3Key, try MayTapi URL as fallback
    if (maytapiAudioUrl && currentAudioUrl !== maytapiAudioUrl) {
      setCurrentAudioUrl(maytapiAudioUrl);
    }
  };

  // Sin fichero NO se devuelve null: eso hacía desaparecer del chat el mensaje
  // entero, y un justificante de pago que no se ve es peor que uno roto.
  if (!currentAudioUrl) return <SinFichero details={details} />;

  return (
    <div className='flex flex-col gap-1 min-w-0 max-w-full'>
      <audio
        controls
        src={currentAudioUrl}
        className='w-56 max-w-full'
        onError={handleAudioError}
      >
        Your browser does not support the audio element.
      </audio>
      {caption && <div className='text-xs text-gray-600 break-words max-w-full'>{caption}</div>}
      {filename && (
        <div className='text-[11px] text-gray-400 break-all max-w-full overflow-hidden' title={filename}>
          {filename}
          {mimeType ? ` · ${mimeType}` : ''}
        </div>
      )}
    </div>
  );
};

export default AudioMessage;
